import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Github, Linkedin, Globe, MapPin, Building, 
  GraduationCap, Code, Trophy, Award, Star, ExternalLink,
  Share2, Briefcase, Calendar, TrendingUp, CheckCircle, Target,
  Loader, Copy, User, Zap, Crown, FolderGit2, Download
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VerifiedBadge from '../components/VerifiedBadge';
import ShareButton from '../components/ShareButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CodeArenaProfilePage = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [verifiedTalent, setVerifiedTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/codearena/profile/${username}`);
      setProfile(res.data.profile);
      setAchievements(res.data.achievements || []);
      setProjects(res.data.projects || []);
      setStats(res.data.stats);
      setVerifiedTalent(res.data.verified_talent || null);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      toast.error('Profile not found');
    } finally {
      setLoading(false);
    }
  };

  const copyProfileLink = () => {
    navigator.clipboard.writeText(`https://evalentx.com/codearena/profile/${username}`);
    toast.success('Profile link copied!');
  };

  const [downloadingCert, setDownloadingCert] = useState(false);
  const downloadCertificate = async () => {
    const token = localStorage.getItem('codearena_token');
    if (!token) {
      toast.error('Please log in as this candidate to download the certificate.');
      return;
    }
    setDownloadingCert(true);
    try {
      const res = await axios.get(`${API}/verified-talent/certificate`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EvalentX_Verified_Talent_${(profile?.full_name || 'candidate').replace(/[^A-Za-z0-9]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Only the verified candidate (logged in) can download this certificate.');
    } finally {
      setDownloadingCert(false);
    }
  };

  const shareToLinkedIn = () => {
    const text = `Check out my developer profile on EvalentX Arena!`;
    const url = `https://evalentx.com/codearena/profile/${username}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const getTierIcon = (tier) => {
    switch (tier?.badge) {
      case 'crown': return Crown;
      case 'star': return Star;
      case 'zap': return Zap;
      case 'trending-up': return TrendingUp;
      case 'target': return Target;
      default: return User;
    }
  };

  const getAchievementIcon = (icon) => {
    const icons = {
      'user-check': User,
      'github': Github,
      'linkedin': Linkedin,
      'award': Award,
      'folder-plus': FolderGit2,
      'check-circle': CheckCircle,
      'zap': Zap,
      'crown': Crown,
      'trophy': Trophy,
      'star': Star,
    };
    return icons[icon] || Award;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f2ef] flex items-center justify-center">
        <Loader className="animate-spin text-[#0a66c2]" size={40} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#f3f2ef] flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
          <User className="mx-auto text-gray-300 mb-4" size={64} />
          <h2 className="text-2xl font-bold text-[#191919] mb-2">Profile Not Found</h2>
          <p className="text-[#666666] mb-4">The user @{username} doesn't exist</p>
          <Link to="/codearena" className="text-[#0a66c2] hover:underline font-medium">
            Back to EvalentX Arena
          </Link>
        </div>
      </div>
    );
  }

  // Calculate tier from stats
  const cvtsApprox = (stats?.total_score || 0);
  const tier = cvtsApprox >= 900 ? { name: 'Legendary', color: '#915907', badge: 'crown' } :
               cvtsApprox >= 750 ? { name: 'Elite', color: '#7c3aed', badge: 'star' } :
               cvtsApprox >= 500 ? { name: 'Expert', color: '#0a66c2', badge: 'zap' } :
               cvtsApprox >= 300 ? { name: 'Advanced', color: '#057642', badge: 'trending-up' } :
               cvtsApprox >= 100 ? { name: 'Intermediate', color: '#915907', badge: 'target' } :
               { name: 'Beginner', color: '#666666', badge: 'user' };

  const TierIcon = getTierIcon(tier);

  return (
    <div className="min-h-screen bg-[#f3f2ef]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <Link 
              to="/codearena" 
              className="flex items-center gap-2 text-[#666666] hover:text-[#191919] transition-colors text-sm"
              data-testid="back-btn"
            >
              <ArrowLeft size={16} />
              EvalentX Arena
            </Link>
            <div className="flex items-center gap-2">
              <button
                onClick={copyProfileLink}
                className="p-2 bg-[#f3f2ef] rounded-lg hover:bg-gray-200 transition-colors border border-gray-200"
                title="Copy Profile Link"
                data-testid="copy-link-btn"
              >
                <Copy size={16} className="text-[#666666]" />
              </button>
              <ShareButton kind="profile" id={username} label="Share" variant="solid" size="md" testid="share-profile-btn" />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Profile Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Profile Card */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              {/* Banner */}
              <div className={`h-20 ${
                verifiedTalent?.is_verified
                  ? 'bg-gradient-to-r from-[#1f1a0c] via-[#3b2a08] to-[#b4881a]'
                  : 'bg-gradient-to-r from-[#0a66c2] to-[#004182]'
              }`} />
              
              <div className="px-5 pb-5">
                {/* Avatar */}
                <div className="relative -mt-12 mb-3">
                  <div className={`w-24 h-24 bg-white rounded-full border-4 shadow-sm flex items-center justify-center text-3xl font-bold ${
                    verifiedTalent?.is_verified
                      ? 'border-amber-300 text-[#8a5a04] ring-4 ring-amber-200/60'
                      : 'border-white text-[#0a66c2]'
                  }`}>
                    {profile.full_name?.charAt(0) || '?'}
                  </div>
                </div>

                <h1
                  className={`text-xl font-semibold ${
                    verifiedTalent?.is_verified
                      ? 'bg-gradient-to-r from-[#8a5a04] via-[#b4881a] to-[#c99b1a] bg-clip-text text-transparent'
                      : 'text-[#191919]'
                  }`}
                  data-testid="profile-name"
                >
                  {profile.full_name}
                </h1>
                <p className="text-[#666666] text-sm">@{profile.username || username}</p>

                {verifiedTalent?.is_verified && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="profile-verified-badge">
                    <VerifiedBadge
                      isVerified
                      planCode={verifiedTalent.plan_code}
                      size="md"
                    />
                    {verifiedTalent.badge_expires_at && (
                      <span className="text-[11px] text-[#666666]">
                        · valid until {new Date(verifiedTalent.badge_expires_at).toLocaleDateString()}
                      </span>
                    )}
                    <button
                      onClick={downloadCertificate}
                      disabled={downloadingCert}
                      className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-400 text-[#1a1203] hover:brightness-105 disabled:opacity-60 shadow-[0_0_0_1px_rgba(202,138,4,0.25)]"
                      data-testid="download-verified-pdf-btn"
                      title="Download Verified Talent PDF"
                    >
                      <Download size={12} />
                      {downloadingCert ? 'Preparing…' : 'PDF'}
                    </button>
                  </div>
                )}
                
                {/* Tier Badge */}
                <div 
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
                  style={{ 
                    backgroundColor: `${tier.color}15`, 
                    color: tier.color 
                  }}
                >
                  <TierIcon size={14} />
                  {tier.name}
                </div>

                {/* Bio */}
                {profile.bio && (
                  <p className="text-[#666666] text-sm mt-4">{profile.bio}</p>
                )}

                {/* Info */}
                <div className="space-y-2.5 mt-4">
                  {profile.college && (
                    <div className="flex items-center gap-2.5 text-[#666666]">
                      <Building size={16} className="text-[#0a66c2]" />
                      <span className="text-sm">{profile.college}</span>
                    </div>
                  )}
                  {profile.department && (
                    <div className="flex items-center gap-2.5 text-[#666666]">
                      <GraduationCap size={16} className="text-[#0a66c2]" />
                      <span className="text-sm">{profile.department} • {profile.year_of_study}</span>
                    </div>
                  )}
                  {profile.location && (
                    <div className="flex items-center gap-2.5 text-[#666666]">
                      <MapPin size={16} className="text-[#b24020]" />
                      <span className="text-sm">{profile.location}</span>
                    </div>
                  )}
                  {profile.looking_for && (
                    <div className="flex items-center gap-2.5 text-[#666666]">
                      <Briefcase size={16} className="text-[#057642]" />
                      <span className="text-sm capitalize">Looking for: {profile.looking_for}</span>
                    </div>
                  )}
                </div>

                {/* Social Links */}
                <div className="flex gap-2 mt-5 pt-4 border-t border-gray-100">
                  {profile.github_url && (
                    <a 
                      href={profile.github_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2.5 bg-[#f3f2ef] rounded-lg hover:bg-gray-200 transition-colors"
                      data-testid="github-link"
                    >
                      <Github size={18} className="text-[#191919]" />
                    </a>
                  )}
                  {profile.linkedin_url && (
                    <a 
                      href={profile.linkedin_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2.5 bg-[#f3f2ef] rounded-lg hover:bg-gray-200 transition-colors"
                      data-testid="linkedin-link"
                    >
                      <Linkedin size={18} className="text-[#0a66c2]" />
                    </a>
                  )}
                  {profile.portfolio_url && (
                    <a 
                      href={profile.portfolio_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2.5 bg-[#f3f2ef] rounded-lg hover:bg-gray-200 transition-colors"
                      data-testid="portfolio-link"
                    >
                      <Globe size={18} className="text-[#0a66c2]" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Stats Card */}
            <div
              className={`border rounded-xl p-5 shadow-sm ${
                verifiedTalent?.is_verified
                  ? 'bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 border-amber-300 shadow-[0_10px_30px_-18px_rgba(202,138,4,0.45)]'
                  : 'bg-white border-gray-200'
              }`}
              data-testid="performance-card"
            >
              <h3 className="font-semibold text-[#191919] mb-4 flex items-center gap-2">
                <TrendingUp size={16} className={verifiedTalent?.is_verified ? 'text-[#8a5a04]' : 'text-[#0a66c2]'} />
                Performance
                {verifiedTalent?.is_verified && (
                  <span className="ml-auto text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded bg-gradient-to-r from-amber-300 to-yellow-400 text-[#1a1203]">Verified</span>
                )}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className={`text-center p-3 rounded-lg ${verifiedTalent?.is_verified ? 'bg-white/70 ring-1 ring-amber-200' : 'bg-[#f3f2ef]'}`}>
                  <div className={`text-xl font-bold ${verifiedTalent?.is_verified ? 'bg-gradient-to-r from-[#8a5a04] to-[#b4881a] bg-clip-text text-transparent' : 'text-[#0a66c2]'}`}>{stats?.total_score || 0}</div>
                  <div className="text-xs text-[#666666]">Total Score</div>
                </div>
                <div className={`text-center p-3 rounded-lg ${verifiedTalent?.is_verified ? 'bg-white/70 ring-1 ring-amber-200' : 'bg-[#f3f2ef]'}`}>
                  <div className={`text-xl font-bold ${verifiedTalent?.is_verified ? 'bg-gradient-to-r from-[#8a5a04] to-[#b4881a] bg-clip-text text-transparent' : 'text-[#915907]'}`}>#{stats?.rank || '-'}</div>
                  <div className="text-xs text-[#666666]">National Rank</div>
                </div>
                <div className={`text-center p-3 rounded-lg ${verifiedTalent?.is_verified ? 'bg-white/70 ring-1 ring-amber-200' : 'bg-[#f3f2ef]'}`}>
                  <div className="text-xl font-bold text-[#057642]">{stats?.problems_solved || 0}</div>
                  <div className="text-xs text-[#666666]">Problems Solved</div>
                </div>
                <div className={`text-center p-3 rounded-lg ${verifiedTalent?.is_verified ? 'bg-white/70 ring-1 ring-amber-200' : 'bg-[#f3f2ef]'}`}>
                  <div className="text-xl font-bold text-[#b24020]">Top {(100 - (stats?.percentile || 0)).toFixed(0)}%</div>
                  <div className="text-xs text-[#666666]">Percentile</div>
                </div>
              </div>
              {verifiedTalent?.is_verified && typeof verifiedTalent.best_score === 'number' && (
                <p className="text-[11px] text-[#8a5a04] mt-3 text-center font-medium">
                  AI Verification Score: <strong>{Math.round(verifiedTalent.best_score)}%</strong> · {verifiedTalent.plan_name || 'Verified'}
                </p>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Tabs */}
            <div className="flex gap-1.5 bg-white border border-gray-200 rounded-xl p-1.5 shadow-sm">
              {[
                { id: 'overview', label: 'Overview', icon: User },
                { id: 'skills', label: 'Skills', icon: Code },
                { id: 'projects', label: 'Projects', icon: FolderGit2 },
                { id: 'achievements', label: 'Achievements', icon: Trophy },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                      activeTab === tab.id
                        ? 'bg-[#0a66c2] text-white'
                        : 'text-[#666666] hover:text-[#191919] hover:bg-gray-50'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon size={14} />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                {/* Skills Summary */}
                {profile.skills && profile.skills.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <h3 className="font-semibold text-[#191919] mb-3 flex items-center gap-2">
                      <Code size={16} className="text-[#0a66c2]" />
                      Skills
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.skills.map((skill, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-[#e8f4f8] text-[#0a66c2] rounded-full text-sm border border-[#0a66c2]/20"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Domains */}
                {profile.domains && profile.domains.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <h3 className="font-semibold text-[#191919] mb-3 flex items-center gap-2">
                      <Target size={16} className="text-[#7c3aed]" />
                      Interested Domains
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.domains.map((domain, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-[#7c3aed]/10 text-[#7c3aed] rounded-full text-sm border border-[#7c3aed]/20"
                        >
                          {domain}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Achievements */}
                {achievements.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <h3 className="font-semibold text-[#191919] mb-3 flex items-center gap-2">
                      <Trophy size={16} className="text-[#915907]" />
                      Recent Achievements
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                      {achievements.slice(0, 6).map((ach, idx) => {
                        const AchIcon = getAchievementIcon(ach.icon);
                        return (
                          <div key={idx} className="flex items-center gap-2.5 p-3 bg-[#f3f2ef] rounded-lg">
                            <div className="w-9 h-9 bg-[#915907]/10 rounded-lg flex items-center justify-center">
                              <AchIcon size={16} className="text-[#915907]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-[#191919] text-sm truncate">{ach.title}</div>
                              <div className="text-xs text-[#666666]">+{ach.points} pts</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Featured Projects */}
                {projects.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <h3 className="font-semibold text-[#191919] mb-3 flex items-center gap-2">
                      <FolderGit2 size={16} className="text-[#057642]" />
                      Featured Projects
                    </h3>
                    <div className="space-y-3">
                      {projects.slice(0, 2).map((project, idx) => (
                        <div key={idx} className="p-4 bg-[#f3f2ef] rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-[#191919]">{project.title}</h4>
                            <div className="flex gap-1.5">
                              {project.github_url && (
                                <a 
                                  href={project.github_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 bg-white rounded hover:bg-gray-200 transition-colors"
                                >
                                  <Github size={14} className="text-[#666666]" />
                                </a>
                              )}
                              {project.live_url && (
                                <a 
                                  href={project.live_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 bg-white rounded hover:bg-gray-200 transition-colors"
                                >
                                  <ExternalLink size={14} className="text-[#0a66c2]" />
                                </a>
                              )}
                            </div>
                          </div>
                          <p className="text-[#666666] text-sm mb-2 line-clamp-2">{project.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {project.tech_stack?.slice(0, 4).map((tech, tidx) => (
                              <span key={tidx} className="px-2 py-0.5 bg-white text-[#666666] rounded text-xs border border-gray-200">
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {projects.length > 2 && (
                      <button
                        onClick={() => setActiveTab('projects')}
                        className="mt-3 text-[#0a66c2] text-sm font-medium hover:underline"
                      >
                        View all {projects.length} projects
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Skills Tab */}
            {activeTab === 'skills' && (
              <div className="space-y-4">
                {/* Programming Languages */}
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold text-[#191919] mb-3">Programming Languages & Skills</h3>
                  {profile.skills && profile.skills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.map((skill, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1.5 bg-[#e8f4f8] text-[#0a66c2] rounded-lg text-sm border border-[#0a66c2]/20 font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#666666] text-sm">No skills added yet</p>
                  )}
                </div>

                {/* Frameworks */}
                {profile.frameworks && profile.frameworks.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <h3 className="font-semibold text-[#191919] mb-3">Frameworks & Libraries</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.frameworks.map((fw, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1.5 bg-[#7c3aed]/10 text-[#7c3aed] rounded-lg text-sm border border-[#7c3aed]/20 font-medium"
                        >
                          {fw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tools */}
                {profile.tools && profile.tools.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <h3 className="font-semibold text-[#191919] mb-3">Tools & Technologies</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.tools.map((tool, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1.5 bg-[#057642]/10 text-[#057642] rounded-lg text-sm border border-[#057642]/20 font-medium"
                        >
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Domains */}
                {profile.domains && profile.domains.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <h3 className="font-semibold text-[#191919] mb-3">Domain Expertise</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.domains.map((domain, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1.5 bg-[#915907]/10 text-[#915907] rounded-lg text-sm border border-[#915907]/20 font-medium"
                        >
                          {domain}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <div className="space-y-3">
                {projects.length > 0 ? projects.map((project, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#0a66c2]/30 hover:shadow-md transition-all shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-[#191919]">{project.title}</h3>
                      <div className="flex gap-1.5">
                        {project.github_url && (
                          <a 
                            href={project.github_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-[#f3f2ef] rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            <Github size={16} className="text-[#666666]" />
                          </a>
                        )}
                        {project.live_url && (
                          <a 
                            href={project.live_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-[#f3f2ef] rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            <ExternalLink size={16} className="text-[#0a66c2]" />
                          </a>
                        )}
                      </div>
                    </div>
                    <p className="text-[#666666] text-sm mb-3">{project.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {project.tech_stack?.map((tech, tidx) => (
                        <span key={tidx} className="px-2.5 py-1 bg-[#f3f2ef] text-[#666666] rounded-lg text-xs border border-gray-200">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )) : (
                  <div className="bg-white border border-gray-200 rounded-xl p-12 text-center shadow-sm">
                    <FolderGit2 className="mx-auto text-gray-300 mb-4" size={48} />
                    <p className="text-[#666666]">No projects added yet</p>
                  </div>
                )}
              </div>
            )}

            {/* Achievements Tab */}
            {activeTab === 'achievements' && (
              <div className="space-y-3">
                {achievements.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-3">
                    {achievements.map((ach, idx) => {
                      const AchIcon = getAchievementIcon(ach.icon);
                      return (
                        <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-[#915907]/30 hover:shadow-md transition-all shadow-sm">
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 bg-[#915907]/10 rounded-xl flex items-center justify-center">
                              <AchIcon size={20} className="text-[#915907]" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="font-semibold text-[#191919] text-sm">{ach.title}</h4>
                                <span className="text-[#915907] font-semibold text-sm">+{ach.points}</span>
                              </div>
                              <p className="text-[#666666] text-xs mb-2">{ach.description}</p>
                              <p className="text-xs text-[#666666]">
                                <Calendar size={10} className="inline mr-1" />
                                {new Date(ach.earned_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-xl p-12 text-center shadow-sm">
                    <Trophy className="mx-auto text-gray-300 mb-4" size={48} />
                    <p className="text-[#666666]">No achievements earned yet</p>
                    <p className="text-[#666666] text-sm mt-1">Complete profile and solve problems to earn badges!</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeArenaProfilePage;
